<template>
  <v-row>
    <v-col cols="8">
      <v-data-table-server :headers="headers"
                           :items="clinics"
                           v-model:items-per-page="itemsPerPage"
                           v-model:sort-by="sortBy"
                           :items-length="totalItems"
                           :loading="loading"
                           :search="search"
                           item-value="id"
                           :row-props="rowProps"
                           :items-per-page-options="[15,25,50,100]"
                           :multi-sort="true"
                           @click:row="handleRowClicked"
                           @update:options="loadItems">
        <template v-slot:top>
          <v-toolbar color="secondary" flat>
            <v-toolbar-title class="text-button">Key Clinics - Potential Partners</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn variant="plain" icon="mdi-file-excel" :disabled="loading" @click.stop="downloadExcel"></v-btn>

            <v-dialog v-model="dialog" max-width="50%">
              <v-card ref="clinic-details">
                <v-card-title>
                  <v-row no-gutters>
                    <v-col>Clinic Details</v-col>
                    <v-col class="text-right">
                      <v-icon size="small" color="warning" v-tooltip="'Imported clinic'" v-if="currentItem.customer">
                        mdi-account-alert-outline
                      </v-icon>
                      <v-icon size="small" color="success" v-tooltip="'Not imported'" v-else>
                        mdi-earth
                      </v-icon>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <h2>{{currentItem.name}}</h2>
                  <br/>
                  <div v-if="currentItem.pdf">
                    <v-btn color="secondary" @click.stop="openFile(currentItem.id, currentItem.pdf)" :disabled="false">Download additional information</v-btn>
                    <br/>
                    <br/>
                  </div>
                  <h3>Therapeutic Area</h3>
                  <p>{{currentItem.therapeuticArea?.join(', ')}}</p>
                  <br/>
                  <h3>Countries</h3>
                  <p>{{currentItem.country?.join(', ')}}</p>
                  <br/>
                  <div v-if="currentItem.countryComment">
                    <h3>Comment to countries</h3>
                    <p>{{currentItem.countryComment}}</p>
                    <br/>
                  </div>
                  <h3>Information</h3>
                  <div v-for="info in currentItem.keyInformation" :key="info">
                    <p>{{info}}</p>
                    <br/>
                  </div>
                  <h3>Publications / Links</h3>
                  <div v-for="pub in currentItem.publicationWebsite" :key="pub">
                    <i>{{pub}}</i>
                    <br/>
                  </div>
                  <br/>
                  <div v-if="currentItem.customer">
                    <h3>Originally delivered to customer</h3>
                    <p>{{customers.get(currentItem.customer)?.name || 'You do not have access to this customer.'}}</p>
                    <br/>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-row no-gutters>
                    <v-col class="text-left">
                      <vue-to-print :content="() => this.$refs['clinic-details'].$el"
                                    :document-title="currentItem.name + '-' + 'clinic-details'"
                                    remove-after-print>
                        <template v-slot:trigger>
                          <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                        </template>
                      </vue-to-print>
                    </v-col>
                    <v-col class="text-right">
                      <v-btn variant="text" @click="close" :disabled="false">Close</v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:[`item.therapeuticArea`]="{ item }">
          <v-chip-group :column="true">
            <v-chip v-for="therapeuticArea in item.therapeuticArea" :key="therapeuticArea" class="me-2" color="primary" size="small"
                    :label="true" @click.stop="toggleAllOneTherapeuticAreas(therapeuticArea)">{{ therapeuticArea }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.country`]="{ item }">
          <v-chip-group :column="true">
            <v-chip v-for="country in item.country" :key="country" class="me-2" color="primary" size="small"
                    :label="true" @click.stop="toggleAllOneCountries(country)">{{ country }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.access`]="{ item }">
          <v-chip color="primary" size="small" :label="true" v-if="item.customer">
            <v-icon class="me-2" size="small" color="warning" v-tooltip="'Imported clinic'">
              mdi-account-alert-outline
            </v-icon>
            Imported
          </v-chip>
          <v-icon size="small" color="primary-darken-1" v-tooltip="'Not imported'" v-else>
            mdi-earth
          </v-icon>
        </template>
        <template v-slot:no-data>
          <v-card-text>No data available</v-card-text>
        </template>
      </v-data-table-server>
    </v-col>
    <v-col cols="4">
      <v-toolbar color="secondary" flat>
        <v-spacer></v-spacer>
        <v-label>Filters</v-label>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Geographical distribution">
            <div>
              <GChart :settings="{ packages: ['geochart'], mapsApiKey: 'AIzaSyC95Y92u-OHlj8KHIODdWMCTqZe0WqZXZ4' }"
                      type="GeoChart"
                      @ready="regionalMapChartReady"
                      :data="regionalMapChartData"
                      :options="regionalMapChartOptions"
                      v-if="regionalMapChartData.length !== 0">
              </GChart>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                          opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto" elevation="0">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row>
                <v-col cols="12">
                  <v-combobox v-model="selectedTherapeuticAreas" :items="therapeuticAreaItems"
                              v-if="therapeuticAreaItems.length > 0"
                              label="Therapeutic Area" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-combobox v-model="selectedCountries" :items="countryItems"
                              v-if="countryItems.length > 0"
                              label="Country" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="clinicsSearch"
                                @update:model-value="clinicsSearchUpdated"
                                label="Search"
                                placeholder="Search"
                                :disabled="!clinics || (clinics.length === 0)"
                                hide-details filled dense></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import {GChart} from 'vue-google-charts'
import { VueToPrint } from "vue-to-print";

const ANY = 'Any'

function debounce(func, timer, timeout = 1000) {
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

function createMap(list, keyExtractor) {
  let map = new Map();
  list.forEach(item => {
    let key = keyExtractor(item);
    map.set(key, item);
  })
  return map
}

export default defineComponent({
  name: 'LandscapingClinicsPage',
  components: {
    GChart,
    VueToPrint
  },
  data: () => ({
    filter_drawer: false,
    dialog: false,
    itemsPerPage: 15,
    sortBy: [{ key: 'name', order: 'asc' }],
    search: '',
    loading: true,
    totalItems: 0,
    currentItem: {},
    headers: [
      {
        title: 'Name',
        align: 'start',
        sortable: true,
        key: 'name',
      },
      { title: 'Therapeutic area', key: 'therapeuticArea', sortable: false },
      { title: 'Countries', key: 'country', sortable: false },
      { title: 'Comment to countries', key: 'countryComment', sortable: true },
      { title: 'Access', key: 'access', sortable: false },
      { title: 'Actions', key: 'actions', sortable: false }
    ],
    clinics: [],
    customers: new Map(),
    therapeuticAreaItems: [ANY],
    selectedTherapeuticAreas: [ANY],
    countryItems: [ANY],
    selectedCountries: [ANY],
    clinicsSearch: '',
    clinicsSearchTimer: undefined,
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    regionalMapChartData: [],
    regionalMapChartOptions: {
      datalessRegionColor: '#647e91',
      defaultColor: '#647e91',
      backgroundColor: '#364c5b',
      colorAxis: {colors: ['#a9a868', '#b2df8a']},
      height: 800,
      legend: 'none'
    }
  }),

  watch: {
    selectedTherapeuticAreas (newSelectedTherapeuticAreas, oldSelectedTherapeuticAreas) {
      if (((newSelectedTherapeuticAreas.length === 1) && (newSelectedTherapeuticAreas[0] === ANY)) ||
          (!newSelectedTherapeuticAreas.includes(ANY) && !oldSelectedTherapeuticAreas.includes(ANY))) {
        this.filterClinics()
        return
      }

      if (newSelectedTherapeuticAreas.includes(ANY) && !oldSelectedTherapeuticAreas.includes(ANY)) {
        this.selectedTherapeuticAreas = [ANY]
      } else if (newSelectedTherapeuticAreas.includes(ANY) && oldSelectedTherapeuticAreas.includes(ANY)) {
        this.selectedTherapeuticAreas = this.selectedTherapeuticAreas.filter(c => c !== ANY)
      }
      this.filterClinics()
    },

    selectedCountries(newSelectedCountries, oldSelectedCountries) {
      if (((newSelectedCountries.length === 1) && (newSelectedCountries[0] === ANY)) ||
          (!newSelectedCountries.includes(ANY) && !oldSelectedCountries.includes(ANY))) {
        this.filterClinics()
        return
      }

      if (newSelectedCountries.includes(ANY) && !oldSelectedCountries.includes(ANY)) {
        this.selectedCountries = [ANY]
      } else if (newSelectedCountries.includes(ANY) && oldSelectedCountries.includes(ANY)) {
        this.selectedCountries = this.selectedCountries.filter(c => c !== ANY)
      }
      this.filterClinics()
    }
  },

  methods: {
    async loadLandscapingClinics(page, itemsPerPage, sortBy) {
      let data = axios.get('/data-landscaping/clinics',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              country: this.selectedCountries.includes(ANY) ? undefined : this.selectedCountries.join(";"),
              search: this.clinicsSearch === '' ? undefined : this.clinicsSearch,
              page: page,
              items_per_page: itemsPerPage,
              sort_by: sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            }
          });
      return data.then((response) => {
        return {
          items: response.data.items,
          total: response.data.total
        }
      });
    },

    async loadLandscapingClinicsMetaData() {
      return axios.get('/data-landscaping/clinic-metadata',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              country: this.selectedCountries.includes(ANY) ? undefined : this.selectedCountries.join(";"),
              search: this.clinicsSearch === '' ? undefined : this.clinicsSearch
            }
          });
    },

    async loadCustomers() {
      return axios.get('/customers',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {}
          });
    },

    downloadExcel() {
      this.loading = true
      axios.get('/data-landscaping/clinics-excel',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              country: this.selectedCountries.includes(ANY) ? undefined : this.selectedCountries.join(";"),
              search: this.clinicsSearch === '' ? undefined : this.clinicsSearch,
              sort_by: this.sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'clinics.xlsx');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.loading = false
          });
    },

    async loadStaticData() {
      return await Promise.all([
        this.loadCustomers(),
        this.loadLandscapingClinicsMetaData()
      ]).then((response) => {
        this.customers = createMap(response[0].data, item => item['id'])
        this.therapeuticAreaItems = response[1].data.allTherapeuticAreas
        this.therapeuticAreaItems.unshift(ANY)
        this.countryItems = response[1].data.allCountries
        this.countryItems.unshift(ANY)
        this.regionalMapChartData = response[1].data.countryCounts;
        console.log('Static data loaded: LandscapingClinics')
      })
    },

    loadItems({ page, itemsPerPage, sortBy }) {
      this.loading = true
      return Promise.all([
          this.loadLandscapingClinics(page, itemsPerPage, sortBy),
          this.loadLandscapingClinicsMetaData()
      ]).then(((response) => {
        this.clinics = response[0].items;
        this.totalItems = response[0].total;
        this.regionalMapChartData = response[1].data.countryCounts;
      })).finally(() => {
        this.loading = false
        console.log('Landscaping clinics loaded')
      })
    },

    filterClinics() {
      // Triggers an event for searching
      this.search = String(Date.now())
    },

    openFile(id, file) {
      let data = axios.get('/data-landscaping/clinic-file',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              clinic_id: id,
              file_name: file
            }
          });
      data.then((response) => {
        window.open(response.data.url, '_blank')
      });
    },

    rowProps() {
      return {
        class: "ciencia_row"
      };
    },

    handleRowClicked(event, row) {
      this.currentItem = row.item
      this.dialog = true
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.currentItem = {}
      })
    },

    toggleAllOneCountries(country) {
      if ((this.selectedCountries.length === 1) && (this.selectedCountries[0] === country)) {
        this.selectedCountries = [...this.countryItems]
      } else {
        this.selectedCountries = [country]
      }
    },

    toggleAllOneTherapeuticAreas(therapeuticArea) {
      if ((this.selectedTherapeuticAreas.length === 1) && (this.selectedTherapeuticAreas[0] === therapeuticArea)) {
        this.selectedTherapeuticAreas = [...this.therapeuticAreaItems]
      } else {
        this.selectedTherapeuticAreas = [therapeuticArea]
      }
    },

    clinicsSearchUpdated() {
      this.clinicsSearchTimer = debounce(() => this.filterClinics(), this.clinicsSearchTimer)()
    },

    async regionalMapChartReady(chart, google) {
      const setRegionalMapChartSelection = (selectedRow) => {
        let selected_region = this.regionalMapChartData[selectedRow + 1][0].f
        if (selected_region !== undefined) {
          this.toggleAllOneCountries(selected_region)
        }
      }

      const regionSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setRegionalMapChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', regionSelectHandler);
    }
  },

  async mounted() {
    await this.loadStaticData()
  }
})
</script>

<style lang="scss" scoped>
.dp__theme_dark {
  --dp-background-color: #364c5b;
  --dp-text-color: #dcdcdc;
  --dp-border-color: #dcdcdc;
}
.error {
  color: indianred;
}

::v-deep .ciencia_row:hover {
  background-color: #566c7b;
}
</style>
